import { hash } from '@ember/helper'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { array } from '@ember/helper'
import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import placeholderImage from 'teacher-ui-client/helpers/placeholder-image'
import imgSizes from 'teacher-ui-client/modifiers/img-sizes'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import perform from 'ember-concurrency/helpers/perform'
import { service } from '@ember/service'
import type BigBookService from 'teacher-ui-client/services/big-books'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type InteractivePreviewService from 'teacher-ui-client/services/interactive-preview'
import { BigBookPreview } from 'teacher-ui-client/interactive-preview/engines'

interface Signature {
  Element: HTMLDivElement
  Args: {
    fadeIn: boolean
    book: BookLibraryBook
    subscriptionType: SubscriptionType
  }
}

export class BigBookCard extends Component<Signature> {
  @service bigBooks!: BigBookService

  @service interactivePreview!: InteractivePreviewService

  @tracked coverLoaded = false

  get hasBookNotes() {
    return Boolean(this.args.subscriptionType && this.args.book.bookMetadata.hasBookNotes?.[this.args.subscriptionType])
  }

  onCoverLoad = () => {
    this.coverLoaded = true
  }

  openBookPreview = () => {
    const preview = new BigBookPreview(
      this,
      this.interactivePreview.curriculum,
      this.args.book.code,
      this.args.subscriptionType,
      this.args.book.bigBookLevels?.[this.args.subscriptionType],
    )
    this.interactivePreview.openPreview(preview, { title: this.args.book.title, startFullscreen: true })
  }

  <template>
    <div class="{{if @fadeIn 'transition-opacity duration-500'}} {{unless this.coverLoaded 'opacity-0'}}" ...attributes>
      <div data-test-book-card-body>
        {{#if @book.bookPreviewImg}}
          {{#if @book.bookFullImg}}
            <img
              role="button"
              class="border-dusty-black-200 h-56 cursor-pointer border object-contain"
              src={{placeholderImage width=@book.bookPreviewImg.width height=@book.bookPreviewImg.height}}
              alt={{@book.title}}
              {{imgSizes
                sizes=(array
                  (hash src=@book.bookPreviewImg.url onLoad=this.onCoverLoad) (hash src=@book.bookFullImg.url)
                )
              }}
              {{on "click" this.openBookPreview}}
            />
          {{/if}}
        {{/if}}
      </div>
      <div data-test-book-card-footer class="relative">
        {{#if this.hasBookNotes}}
          <button
            type="button"
            {{on "click" (perform this.bigBooks.downloadBookNotes @book.code)}}
            data-test-notes-for-book={{@book.code}}
            class="bg-cornflower-blue-100 text-cornflower-blue-350 absolute -top-5 right-0 cursor-pointer rounded-full px-3 py-1 font-semibold whitespace-nowrap shadow-md select-none"
          >
            <span class="pr-1">
              <PhosphorIcon @icon="phosphor-file-text-fill" />
            </span>
            <span>
              {{t "bigBooks.card.bookNotes"}}
            </span>
          </button>
        {{/if}}
      </div>
    </div>
  </template>
}
